package de.oevbs.kunde.kundenportal2.util

import de.oevbs.kunde.kundenportal2.PortalMessages
import de.oevbs.kunde.kundenportal2.components.GlobalServiceMessagesStore
import de.oevbs.sfa.communication.ClientResponse
import de.oevbs.sfa.communication.WAFRuleException
import de.oevbs.sfa.communication.onlyValidations
import io.ktor.client.plugins.*
import io.ktor.utils.io.*

/**
 * Führt einen Client-Call durch und behandelt die dabei auftretenden Ausnahmen.
 * Fehler werden in die Konsole geloggt und zum globalen
 * [GlobalServiceMessagesStore] hinzugefügt.
 *
 * Zur normalen Fehlerbehandlung der Response und zur Extraktion des Datenmodells im Erfolgsfall wird
 * die Funktion [checkResponseAndGetModel] aufgerufen.
 *
 * @return T wenn keine Ausnahmen und keine Fehler auftreten
 *         null sonst
 */
suspend fun <T : Any> tryCatchAndHandleErrors(action: String, clientCall: suspend () -> ClientResponse<T>) : T? {
    val response: ClientResponse<T>
    try {
        response = clientCall()
    } catch (ex: HttpRequestTimeoutException) {
        console.error("Timeout bei $action: ${ex.message}")
        GlobalServiceMessagesStore.add(PortalMessages.connectionError)
        return null
    } catch (ex: WAFRuleException) {
        console.error("WAFRuleException bei $action: ${ex.message}")
        GlobalServiceMessagesStore.add(PortalMessages.securityError)
        return null
    } catch (ex: CancellationException) {
        // FIXME: Entfernen, sobald der Authentifizierungsprozess auf Azure B2C ODER den von der SFA2 vorgedachten
        //  Prozess umgestellt wurde.
        // Job wurde durch die PortalSessionMiddleware gecancelt. Da es sich dort um einen Workaround zum Vorschalten
        // der Login-Seite handelt (siehe Kommentare an der entsprechenden Stelle), muss hier entsprechend reagiert
        // werden.
        console.log("Request wurde abgebrochen, da die Nutzersession abgelaufen ist.")
        return null
    } catch (ex: Throwable) {
        console.error("Throwable bei $action: ${ex.message}")
        GlobalServiceMessagesStore.add(PortalMessages.noSuccess)
        return null
    }
    return checkResponseAndGetModel(response)
}

/**
 * Behandelt eine ClientResponse inkl. Fehler und Warnungen. Diese Funktion sollte
 * für alle Client-Aufrufe im Kundenportal-UI verwendet werden.
 *
 * Fehlermeldungen aus der Response werden dem globalen Fehlerstore
 * [GlobalServiceMessagesStore] hinzugefügt (auch Infos und Warnungen).
 *
 * Wenn ein Datenmodell != null vorhanden ist, wird dieses zurückgegeben.
 * Sonst wird null zurückgegeben.
 */
private fun <T : Any> checkResponseAndGetModel(response: ClientResponse<T>): T? {

    if (response.isFailure) {
        if (response.messages.isEmpty()) {
            GlobalServiceMessagesStore.add(PortalMessages.noSuccess)
            console.error("Fehler - ClientResponse ist fehlgeschlagen, es wurden aber keine Fehlermeldungen gefunden.")
        } else {
            //Dem User werden nur die vom Kupo formulierten Fehler angezeigt
            val kupoMessages = response.messages.onlyValidations

            //Falls keine Kupo-Meldungen ankamen, eine allgemeine Nachricht anstelle der Default-Nachrichten anzeigen
            if (kupoMessages.isEmpty()) {
                GlobalServiceMessagesStore.add(PortalMessages.unknownError)
                console.error("Fehler - ClientResponse ist fehlgeschlagen. Es wurden keine Meldungen vom Typ ServiceMessage.Validation gefunden.")
            } else {
                GlobalServiceMessagesStore.addList(kupoMessages)
            }

            //Alle Fehler loggen
            response.messages.forEach {
                console.error("Fehlermeldung vom Service: ${it.message}")
            }
        }
        return null
    } else {
        // Infos und Warnungen anzeigen
        GlobalServiceMessagesStore.addList(response.messages)
        return response.getOrThrow()
    }
}